import { useState, useEffect } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    Button, Input, FormControl, FormLabel, Select, Textarea, VStack, HStack, Box, Text,
    Table, Thead, Tbody, Tr, Th, Td, Badge, Alert, AlertIcon, NumberInput, NumberInputField,
    Divider, useToast, Flex, Heading, Checkbox,
} from '@chakra-ui/react';
import { validarVendaParaDevolucaoManual, criarDevolucaoManual } from '../../services/api';
import ProdutoImagem from '../ProdutoImagem';

const STATUS_OPTIONS = [
    ['opened', 'Aberta'],
    ['label_generated', 'Etiqueta gerada'],
    ['shipped', 'Enviada'],
    ['delivered', 'Entregue'],
    ['closed', 'Encerrada'],
    ['not_delivered', 'Não entregue'],
    ['cancelled', 'Cancelada'],
    ['failed', 'Falhada'],
    ['expired', 'Expirada'],
];

const MONEY_OWNER_OPTIONS = [
    ['', 'Selecione...'],
    ['buyer', 'Devolvido ao cliente'],
    ['seller', 'Valor recuperado'],
    ['unknown', 'Indefinido'],
];

const STATUS_MONEY_OPTIONS = [
    ['', 'Não informado'],
    ['refunded', 'Reembolsado'],
    ['available', 'Disponível'],
    ['retained', 'Retido'],
];

const SHIPPING_PAID_BY_OPTIONS = [
    ['', 'Não informado'],
    ['buyer', 'Comprador'],
    ['seller', 'Vendedor'],
];

const PROBLEMAS_SUGERIDOS = [
    ['', 'Escolher motivo pronto (opcional)'],
    ['A minha compra chegou em boas condições, mas eu não a quero mais', 'Arrependimento — chegou bem mas não quer mais'],
    ['Me arrependi da compra', 'Arrependimento — simples'],
    ['Informava ser compatível com meu veículo, mas não é', 'Não é compatível com o veículo'],
    ['É incompatível com meu veículo', 'Incompatível com o veículo'],
    ['Descumpre as características do anúncio', 'Descumpre as características do anúncio'],
    ['Não é o que dizia o anúncio', 'Não é o que dizia o anúncio'],
    ['Não era da marca original', 'Não era da marca original'],
    ['Recebi outro tipo de produto', 'Recebi outro tipo de produto'],
    ['Recebi um produto diferente do que comprei', 'Recebi produto diferente do comprado'],
    ['Não é da cor, tamanho ou modelo que escolhi', 'Cor / tamanho / modelo errado'],
    ['É da cor / tamanho / modelo escolhido, porém não serviu', 'Não serviu (mesmo sendo o certo)'],
    ['A embalagem estava danificada', 'Embalagem danificada'],
    ['O produto chegou avariado', 'Produto chegou avariado'],
    ['Chegou bem', 'Chegou bem'],
];

const RESOLUCOES_SUGERIDAS = [
    ['', 'Escolher resolução pronta (opcional)'],
    ['Devolução finalizada com reembolso para o comprador', 'Devolução finalizada com reembolso para o comprador'],
    ['Item devolvido', 'Item devolvido'],
    ['Prazo de devolução expirou', 'Prazo de devolução expirou'],
    ['Devolução cancelada', 'Devolução cancelada'],
    ['Pagamento reembolsado', 'Pagamento reembolsado'],
    ['Comprador preferiu ficar com o produto', 'Comprador preferiu ficar com o produto'],
    ['Decisão por cobertura (ML)', 'Decisão por cobertura (ML)'],
    ['Decisão do armazém/logística', 'Decisão do armazém/logística'],
    ['Prazo de troca expirou', 'Prazo de troca expirou'],
    ['Reclamação aberta por engano', 'Reclamação aberta por engano'],
    ['Item trocado', 'Item trocado'],
    ['Sem cobertura de compra garantida', 'Sem cobertura de compra garantida'],
];

const formatBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v || 0));
const hojeISO = () => new Date().toISOString().slice(0, 10);
const addDiasUteis = (baseISO, dias) => {
    if (!baseISO) return '';
    const d = new Date(baseISO + 'T00:00:00');
    let adicionados = 0;
    while (adicionados < dias) {
        d.setDate(d.getDate() + 1);
        const dow = d.getDay();
        if (dow !== 0 && dow !== 6) adicionados++;
    }
    return d.toISOString().slice(0, 10);
};

const NovaDevolucaoManualModal = ({ isOpen, onClose, onCreated }) => {
    const toast = useToast();
    const [step, setStep] = useState(1);
    const [idVendaInput, setIdVendaInput] = useState('');
    const [buscando, setBuscando] = useState(false);
    const [dadosVenda, setDadosVenda] = useState(null);
    const [itensSelecionados, setItensSelecionados] = useState({});
    const [form, setForm] = useState({
        problem: 'Recebi outro tipo de produto', status: 'opened', money_owner: 'buyer',
        title: '', description: '', resolution: 'Devolução finalizada com reembolso para o comprador',
        status_money: 'refunded',
        shipping_paid_by: 'seller',
        review_due_date: addDiasUteis(hojeISO(), 14), date_created: hojeISO(),
    });
    const [salvando, setSalvando] = useState(false);
    const [dueDateTouched, setDueDateTouched] = useState(false);

    const resetar = () => {
        setStep(1); setIdVendaInput(''); setDadosVenda(null); setItensSelecionados({}); setDueDateTouched(false);
        setForm({
            problem: '', status: 'opened', money_owner: 'buyer',
            title: '', description: '', resolution: '',
            status_money: 'refunded',
            shipping_paid_by: 'seller',
            review_due_date: addDiasUteis(hojeISO(), 14), date_created: hojeISO(),
        });
    };

    const fechar = () => { resetar(); onClose && onClose(); };

    const buscarVenda = async () => {
        if (!idVendaInput.trim()) {
            toast({ title: 'Informe o ID da venda', status: 'warning', duration: 3000 });
            return;
        }
        setBuscando(true);
        try {
            const resp = await validarVendaParaDevolucaoManual(idVendaInput.trim());
            if (!resp?.elegivel) {
                toast({
                    title: 'Venda não elegível',
                    description: resp?.error || 'Verifique o ID e tente novamente.',
                    status: 'error', duration: 5000, isClosable: true,
                });
                return;
            }
            setDadosVenda(resp);
            const init = {};
            for (const it of resp.itens) {
                if (it.quantidade_disponivel > 0) {
                    const qty = it.quantidade_disponivel;
                    init[it.produto_id] = {
                        selecionado: true,
                        qty,
                        refund: ((it.preco_unitario || 0) * qty).toFixed(2),
                        shipping: '',
                        refundTouched: false,
                    };
                }
            }
            setItensSelecionados(init);
            setStep(2);
        } finally { setBuscando(false); }
    };

    const toggleItem = (produtoId) => {
        setItensSelecionados(prev => ({
            ...prev,
            [produtoId]: { ...prev[produtoId], selecionado: !prev[produtoId]?.selecionado },
        }));
    };

    const itensDisponiveis = (dadosVenda?.itens || []).filter(it => it.quantidade_disponivel > 0);
    const selecionadosCount = itensDisponiveis.filter(it => itensSelecionados[it.produto_id]?.selecionado).length;
    const todosMarcados = itensDisponiveis.length > 0 && selecionadosCount === itensDisponiveis.length;
    const algunsMarcados = selecionadosCount > 0 && !todosMarcados;

    useEffect(() => {
        if (dueDateTouched) return;
        if (!form.date_created) return;
        setForm(f => ({ ...f, review_due_date: addDiasUteis(f.date_created, 14) }));
    }, [form.date_created, dueDateTouched]);

    const toggleTodos = () => {
        const marcar = !todosMarcados;
        setItensSelecionados(prev => {
            const novo = { ...prev };
            for (const it of itensDisponiveis) {
                const qty = prev[it.produto_id]?.qty ?? it.quantidade_disponivel;
                const refundPrev = prev[it.produto_id]?.refund;
                const refundTouched = !!prev[it.produto_id]?.refundTouched;
                novo[it.produto_id] = {
                    selecionado: marcar,
                    qty,
                    refund: refundTouched ? refundPrev : ((it.preco_unitario || 0) * qty).toFixed(2),
                    shipping: prev[it.produto_id]?.shipping ?? '',
                    refundTouched,
                };
            }
            return novo;
        });
    };

    const setQtyItem = (produtoId, valor, max, precoUnit) => {
        const n = Math.max(0, Math.min(Number(valor) || 0, max));
        setItensSelecionados(prev => {
            const atual = prev[produtoId] || {};
            const next = { ...atual, qty: n };
            if (!atual.refundTouched) {
                next.refund = ((precoUnit || 0) * n).toFixed(2);
            }
            return { ...prev, [produtoId]: next };
        });
    };

    const setRefundItem = (produtoId, valor) => {
        setItensSelecionados(prev => ({
            ...prev,
            [produtoId]: { ...prev[produtoId], refund: valor, refundTouched: true },
        }));
    };

    const setShippingItem = (produtoId, valor) => {
        setItensSelecionados(prev => ({
            ...prev,
            [produtoId]: { ...prev[produtoId], shipping: valor },
        }));
    };

    const itensParaEnvio = () => {
        const out = [];
        for (const [produtoId, info] of Object.entries(itensSelecionados)) {
            if (info?.selecionado && (info?.qty ?? 0) > 0) {
                const payload = { produto_id: Number(produtoId), return_quantity: Number(info.qty) };
                if (info.refund !== '' && info.refund !== null && info.refund !== undefined) {
                    payload.refund_amount = Number(info.refund);
                }
                if (info.shipping !== '' && info.shipping !== null && info.shipping !== undefined) {
                    payload.shipping_cost = Number(info.shipping);
                }
                out.push(payload);
            }
        }
        return out;
    };

    const valorSelecionado = () => {
        if (!dadosVenda?.itens) return 0;
        let total = 0;
        for (const it of dadosVenda.itens) {
            const sel = itensSelecionados[it.produto_id];
            if (sel?.selecionado) total += (it.preco_unitario || 0) * (sel.qty || 0);
        }
        return total;
    };

    const totalReembolsoSelecionado = () => {
        let total = 0;
        for (const info of Object.values(itensSelecionados)) {
            if (info?.selecionado) total += Number(info.refund || 0);
        }
        return total;
    };

    const totalFreteSelecionado = () => {
        let total = 0;
        for (const info of Object.values(itensSelecionados)) {
            if (info?.selecionado) total += Number(info.shipping || 0);
        }
        return total;
    };

    const salvar = async () => {
        const itens = itensParaEnvio();
        if (itens.length === 0) {
            toast({ title: 'Selecione ao menos 1 item com quantidade > 0', status: 'warning', duration: 3000 });
            return;
        }
        if (!form.problem.trim()) {
            toast({ title: 'Descreva o problema da devolução', status: 'warning', duration: 3000 });
            return;
        }
        if (!form.money_owner) {
            toast({ title: 'Selecione o destino do dinheiro', status: 'warning', duration: 3000 });
            return;
        }

        const payload = {
            id_venda: dadosVenda.venda.id,
            itens,
            problem: form.problem.trim(),
            status: form.status,
            money_owner: form.money_owner,
        };
        if (form.title) payload.title = form.title;
        if (form.description) payload.description = form.description;
        if (form.resolution) payload.resolution = form.resolution;
        if (form.status_money) payload.status_money = form.status_money;
        if (form.shipping_paid_by) payload.shipping_paid_by = form.shipping_paid_by;
        if (form.review_due_date) payload.review_due_date = form.review_due_date;
        if (form.date_created) payload.date_created = form.date_created;

        setSalvando(true);
        try {
            const resp = await criarDevolucaoManual(payload);
            if (resp?.success) {
                const qtd = Array.isArray(resp.ids) ? resp.ids.length : 1;
                toast({
                    title: qtd > 1 ? `${qtd} devoluções manuais criadas` : 'Devolução manual criada',
                    status: 'success', duration: 3000,
                });
                onCreated && onCreated(resp);
                fechar();
            } else {
                toast({
                    title: 'Falha ao criar devolução',
                    description: resp?.error || 'Erro desconhecido',
                    status: 'error', duration: 6000, isClosable: true,
                });
            }
        } finally { setSalvando(false); }
    };

    return (
        <Modal isOpen={isOpen} onClose={fechar} size="4xl" scrollBehavior="inside" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Nova devolução manual (ECOMMERCE / TAGA)</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    {step === 1 && (
                        <VStack spacing={4} align="stretch">
                            <Alert status="info" borderRadius="md">
                                <AlertIcon />
                                <Text fontSize="sm">
                                    Informe a referência da venda (ECOMMERCE ou TAGA). Os itens, cliente e valores serão carregados automaticamente.
                                </Text>
                            </Alert>
                            <FormControl isRequired>
                                <FormLabel>ID da venda</FormLabel>
                                <HStack>
                                    <Input
                                        value={idVendaInput}
                                        onChange={(e) => setIdVendaInput(e.target.value)}
                                        placeholder="Ex: 17205, ORD-12345"
                                        onKeyDown={(e) => { if (e.key === 'Enter') buscarVenda(); }}
                                    />
                                    <Button
                                        colorScheme="orange"
                                        onClick={buscarVenda}
                                        isLoading={buscando}
                                        minW="120px"
                                    >
                                        Buscar
                                    </Button>
                                </HStack>
                            </FormControl>
                        </VStack>
                    )}

                    {step === 2 && dadosVenda && (
                        <VStack spacing={4} align="stretch">
                            <Box p={3} bg="gray.50" borderRadius="md" borderWidth="1px">
                                <HStack justify="space-between" wrap="wrap">
                                    <Box>
                                        <Text fontSize="xs" color="gray.500">Venda</Text>
                                        <Text fontWeight="bold">#{dadosVenda.venda.id} <Badge colorScheme="purple" ml={2}>{dadosVenda.venda.origem}</Badge></Text>
                                    </Box>
                                    <Box>
                                        <Text fontSize="xs" color="gray.500">Cliente</Text>
                                        <Text fontSize="sm">{`${dadosVenda.cliente?.first_name || ''} ${dadosVenda.cliente?.last_name || ''}`.trim() || 'N/I'}</Text>
                                        <Text fontSize="xs" color="gray.600">{dadosVenda.cliente?.doc_number || ''}</Text>
                                    </Box>
                                </HStack>
                                {dadosVenda.devolucoes_existentes?.length > 0 && (
                                    <Alert status="warning" mt={2} borderRadius="md" fontSize="xs">
                                        <AlertIcon />
                                        Esta venda já possui {dadosVenda.devolucoes_existentes.length} devolução(ões) manual(is). Os itens disponíveis já consideram o que foi devolvido.
                                    </Alert>
                                )}
                            </Box>

                            <Box>
                                <Heading size="sm" mb={2}>Itens da venda</Heading>
                                <Box overflowX="auto" borderWidth="1px" borderRadius="md">
                                <Table size="sm" variant="simple">
                                    <Thead>
                                        <Tr>
                                            <Th>
                                                <Checkbox
                                                    isChecked={todosMarcados}
                                                    isIndeterminate={algunsMarcados}
                                                    isDisabled={itensDisponiveis.length === 0}
                                                    onChange={toggleTodos}
                                                    colorScheme="orange"
                                                />
                                            </Th>
                                            <Th>Produto</Th>
                                            <Th isNumeric>Preço un.</Th>
                                            <Th isNumeric>Qtd. total</Th>
                                            <Th isNumeric>Já devolv.</Th>
                                            <Th isNumeric>Devolver</Th>
                                            <Th isNumeric>Reemb. (R$)</Th>
                                            <Th isNumeric>Frete (R$)</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {dadosVenda.itens.map((it) => {
                                            const sel = itensSelecionados[it.produto_id] || {};
                                            const disp = it.quantidade_disponivel;
                                            return (
                                                <Tr key={it.produto_id} opacity={disp <= 0 ? 0.5 : 1}>
                                                    <Td>
                                                        <Checkbox
                                                            isDisabled={disp <= 0}
                                                            isChecked={!!sel.selecionado}
                                                            onChange={() => toggleItem(it.produto_id)}
                                                            colorScheme="orange"
                                                        />
                                                    </Td>
                                                    <Td minW={{ base: '200px', md: '260px' }}>
                                                        <HStack align="flex-start" spacing={2}>
                                                            <Box flexShrink={0}>
                                                                <ProdutoImagem
                                                                    produto={it}
                                                                    width={{ base: '44px', md: '56px' }}
                                                                    height={{ base: '44px', md: '56px' }}
                                                                />
                                                            </Box>
                                                            <Box>
                                                                <Text fontSize="sm" fontWeight="semibold" noOfLines={2}>{it.nome}</Text>
                                                                <Text fontSize="10px" color="gray.500">SKU: {it.sku} · Cód: {it.codigo_global}</Text>
                                                                {it.codigo_fornecedor && <Text fontSize="10px" color="gray.600">Cód. Forn: {it.codigo_fornecedor}</Text>}
                                                                {it.codigo_fabricante && <Text fontSize="10px" color="gray.600">Cód. Fab: {it.codigo_fabricante}</Text>}
                                                                <HStack spacing={2} mt="2px" flexWrap="wrap">
                                                                    {it.fornecedor && <Badge colorScheme="blue" fontSize="9px">{it.fornecedor}</Badge>}
                                                                    {it.nome_fabricante && <Badge colorScheme="gray" fontSize="9px">{it.nome_fabricante}</Badge>}
                                                                </HStack>
                                                            </Box>
                                                        </HStack>
                                                    </Td>
                                                    <Td isNumeric>{formatBRL(it.preco_unitario)}</Td>
                                                    <Td isNumeric>{it.quantidade_total}</Td>
                                                    <Td isNumeric>{it.quantidade_devolvida}</Td>
                                                    <Td isNumeric>
                                                        <NumberInput
                                                            size="xs"
                                                            min={0}
                                                            max={disp}
                                                            step={1}
                                                            value={sel.qty ?? disp}
                                                            onChange={(v) => setQtyItem(it.produto_id, v, disp, it.preco_unitario)}
                                                            isDisabled={!sel.selecionado || disp <= 0}
                                                            maxW="70px"
                                                            ml="auto"
                                                        >
                                                            <NumberInputField textAlign="right" />
                                                        </NumberInput>
                                                    </Td>
                                                    <Td isNumeric>
                                                        <Input
                                                            size="xs"
                                                            type="number" step="0.01" min="0"
                                                            value={sel.refund ?? ''}
                                                            onChange={(e) => setRefundItem(it.produto_id, e.target.value)}
                                                            isDisabled={!sel.selecionado || disp <= 0}
                                                            maxW="90px"
                                                            ml="auto"
                                                            textAlign="right"
                                                            placeholder="0,00"
                                                        />
                                                    </Td>
                                                    <Td isNumeric>
                                                        <Input
                                                            size="xs"
                                                            type="number" step="0.01" min="0"
                                                            value={sel.shipping ?? ''}
                                                            onChange={(e) => setShippingItem(it.produto_id, e.target.value)}
                                                            isDisabled={!sel.selecionado || disp <= 0}
                                                            maxW="80px"
                                                            ml="auto"
                                                            textAlign="right"
                                                            placeholder="0,00"
                                                        />
                                                    </Td>
                                                </Tr>
                                            );
                                        })}
                                    </Tbody>
                                </Table>
                                </Box>
                                <Flex justify="flex-end" gap={4} mt={2} wrap="wrap">
                                    <Text fontSize="xs" color="gray.600">
                                        Produtos: <Text as="span" fontWeight="bold" color="gray.800">{formatBRL(valorSelecionado())}</Text>
                                    </Text>
                                    <Text fontSize="xs" color="gray.600">
                                        Reembolso: <Text as="span" fontWeight="bold" color="gray.800">{formatBRL(totalReembolsoSelecionado())}</Text>
                                    </Text>
                                    <Text fontSize="xs" color="gray.600">
                                        Frete: <Text as="span" fontWeight="bold" color="gray.800">{formatBRL(totalFreteSelecionado())}</Text>
                                    </Text>
                                </Flex>
                                <Text fontSize="10px" color="gray.500" mt={1}>
                                    Cada item selecionado vira uma devolução separada (1 devolução = 1 item).
                                </Text>
                            </Box>

                            <Divider />

                            <Heading size="sm">Detalhes da devolução</Heading>

                            <FormControl isRequired>
                                <FormLabel>Problema (motivo)</FormLabel>
                                <Select
                                    mb={2} size="sm"
                                    value=""
                                    onChange={(e) => { if (e.target.value) setForm({ ...form, problem: e.target.value }); }}
                                >
                                    {PROBLEMAS_SUGERIDOS.map(([v, l]) => <option key={l} value={v}>{l}</option>)}
                                </Select>
                                <Textarea
                                    value={form.problem}
                                    onChange={(e) => setForm({ ...form, problem: e.target.value })}
                                    placeholder="Ex: Produto chegou danificado"
                                    rows={2}
                                />
                            </FormControl>

                            <HStack spacing={3} align="flex-start">
                                <FormControl isRequired>
                                    <FormLabel>Status</FormLabel>
                                    <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                                        {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                    </Select>
                                </FormControl>
                                <FormControl isRequired>
                                    <FormLabel>Destino do dinheiro</FormLabel>
                                    <Select value={form.money_owner} onChange={(e) => setForm({ ...form, money_owner: e.target.value })}>
                                        {MONEY_OWNER_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                    </Select>
                                </FormControl>
                            </HStack>

                            <HStack spacing={3} align="flex-start">
                                <FormControl>
                                    <FormLabel>Status do dinheiro</FormLabel>
                                    <Select value={form.status_money} onChange={(e) => setForm({ ...form, status_money: e.target.value })}>
                                        {STATUS_MONEY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                    </Select>
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Envio pago por</FormLabel>
                                    <Select value={form.shipping_paid_by} onChange={(e) => setForm({ ...form, shipping_paid_by: e.target.value })}>
                                        {SHIPPING_PAID_BY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                    </Select>
                                </FormControl>
                            </HStack>

                            <HStack spacing={3} align="flex-start">
                                <FormControl>
                                    <FormLabel>Prazo para revisar</FormLabel>
                                    <Input
                                        type="date"
                                        value={form.review_due_date}
                                        onChange={(e) => { setDueDateTouched(true); setForm({ ...form, review_due_date: e.target.value }); }}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Data de criação (opcional)</FormLabel>
                                    <Input
                                        type="date"
                                        value={form.date_created}
                                        onChange={(e) => setForm({ ...form, date_created: e.target.value })}
                                    />
                                </FormControl>
                            </HStack>

                            <FormControl>
                                <FormLabel>Título (opcional)</FormLabel>
                                <Input
                                    value={form.title}
                                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                                    placeholder="Ex: Devolução solicitada pelo cliente"
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Descrição (opcional)</FormLabel>
                                <Textarea
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    rows={2}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Resolução (opcional)</FormLabel>
                                <Select
                                    mb={2} size="sm"
                                    value=""
                                    onChange={(e) => { if (e.target.value) setForm({ ...form, resolution: e.target.value }); }}
                                >
                                    {RESOLUCOES_SUGERIDAS.map(([v, l]) => <option key={l} value={v}>{l}</option>)}
                                </Select>
                                <Textarea
                                    value={form.resolution}
                                    onChange={(e) => setForm({ ...form, resolution: e.target.value })}
                                    rows={2}
                                />
                            </FormControl>
                        </VStack>
                    )}
                </ModalBody>
                <ModalFooter>
                    {step === 2 && (
                        <Button variant="ghost" mr="auto" onClick={() => setStep(1)}>
                            Voltar
                        </Button>
                    )}
                    <Button variant="outline" mr={2} onClick={fechar}>Cancelar</Button>
                    {step === 1 && (
                        <Button colorScheme="orange" onClick={buscarVenda} isLoading={buscando}>
                            Continuar
                        </Button>
                    )}
                    {step === 2 && (
                        <Button colorScheme="orange" onClick={salvar} isLoading={salvando}>
                            Cadastrar devolução
                        </Button>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default NovaDevolucaoManualModal;
